import type { MessageKey } from "@expertloop/i18n";
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { adminApi } from "../services/api";
import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { AdminChatMedia, MediationCaseView, MediationDecision, MediationDossierView, MediationReportView, MediationTranslationView, SupportDecisionOutcome } from "@expertloop/shared";
import { MediationCasePanel } from "../features/mediation/MediationCasePanel";
import { MediationTranslationBar } from "../features/mediation/MediationTranslationBar";
import { TranslationNote } from "../features/mediation/TranslationNote";
import { MediationSnapshot } from "../features/mediation/MediationSnapshot";
import { MediationReportPanel } from "../features/mediation/MediationReportPanel";
import { MediationPricePanel } from "../features/mediation/MediationPricePanel";
import { MediationTimelinePanel } from "../features/mediation/MediationTimelinePanel";
import { MediationDecisionPanel } from "../features/mediation/MediationDecisionPanel";
import { CollapsibleCard } from "../features/mediation/CollapsibleCard";
import { MediaLinks } from "../features/mediation/MediaLinks";
import { ResizableColumns } from "../features/mediation/ResizableColumns";
import { useAttachmentViewer } from "../features/mediation/useAttachmentViewer";
import { MediationConversationPanel, type ConversationFocus } from "../features/mediation/MediationConversationPanel";
import { buildConversation, conversationRef } from "../features/mediation/conversation";
import { pickTranslation, type TextKey, type TranslationViewMode } from "../features/mediation/translations";

const DELIVERY_ACTION_KEYS: Record<string, MessageKey> = {
	delivered: "adminChatDetail.action.delivered",
	redelivered: "adminChatDetail.action.redelivered",
	rejected: "adminChatDetail.action.rejected",
	confirmed: "adminChatDetail.action.confirmed",
	mediation_requested: "adminChatDetail.action.mediation_requested",
	mediation_resolved: "adminChatDetail.action.mediation_resolved",
	mediation_reopened: "adminChatDetail.action.mediation_reopened",
	viewed: "adminChatDetail.action.viewed",
	auto_accepted: "adminChatDetail.action.auto_accepted",
	complement_delivered: "adminChatDetail.action.complement_delivered",
	complement_acknowledged: "adminChatDetail.action.complement_acknowledged",
	chat_reopened: "adminChatDetail.action.chat_reopened",
};

interface ResolveResult {
	success: boolean;
	order: { id: string; status: string };
	decision: MediationDecision;
	amountToClient?: number;
	amountToExpert?: number;
	/**
	 * Faits financiers de la seule décision qui vient d'être rendue.
	 *
	 * La confirmation affichait auparavant le montant autorisé de la commande,
	 * c'est-à-dire le plafond bloqué sur la carte du client : sur une vente de
	 * cent euros encaissée à quatre-vingts, elle annonçait cent euros rendus là
	 * où le support en avait accordé dix.
	 */
	outcome?: SupportDecisionOutcome;
}

interface ChatDetails {
	chat: {
		id: string;
		expertId: string;
		productId: string;
		clientEmail: string;
		clientName?: string;
		status: string;
		requestMessage: unknown;
		acceptedAt?: string;
		completedAt?: string;
		declineReason?: string;
		deliveredAt?: string;
		clientConfirmedAt?: string;
		closedAt?: string;
		paymentAuthorizedAt?: string;
		abandonedAt?: string;
		createdAt: string;
		updatedAt: string;
	};
	expert: {
		id: string;
		email: string;
		firstName: string;
		lastName: string;
		displayName?: string;
		avatarUrl?: string;
		stripeOnboardingCompleted?: boolean;
		averageRating?: number;
		totalRatings?: number;
		createdAt: string;
	};
	client: {
		id?: string;
		email: string;
		firstName?: string;
		lastName?: string;
		name?: string;
		isGuest?: boolean;
		createdAt?: string;
	};
	product: {
		id: string;
		name: string;
		description: string;
		price: number;
		currency: string;
		maxDeliveryHours: number;
	};
	/** Dossier de médiation (MEDIATION-1) : présent quand la conversation a une médiation. */
	mediationCase?: MediationCaseView;
	/** Traductions du dossier pour le support (LANG-3) : référence, langue du dossier, textes. */
	mediationTranslations?: MediationTranslationView;
	/** Chronologie et historique du prix (MEDIATIONUX1). */
	mediationDossier?: MediationDossierView;
	/** Rapport d'instruction IA dans la langue de référence (MEDIATIONUX2). */
	mediationReport?: MediationReportView;
	orders: Array<{
		id: string;
		amount: number;
		currency: string;
		platformFee: number;
		status: string;
		/** Somme réellement encaissée ; absente tant que la vente ne l'est pas. */
		capturedAmount?: number;
		/** Somme déjà rendue au client, cumulée. */
		refundedAmount?: number;
		platformFeeAmount?: number;
		/** Décision du support : sa présence signifie que la médiation est close. */
		supportDecision?: { decision: MediationDecision; message: string; resolvedAt: string };
		authorizedAt?: string;
		capturedAt?: string;
		canceledAt?: string;
		cancelReason?: string;
		canceledBy?: string;
		mediationRequestedAt?: string;
		mediationRequestedBy?: string;
		mediationReason?: string;
		deliveryHistory?: Array<{
			action: string;
			by: string;
			reason?: string;
			timestamp: string;
		}>;
		rating?: number;
		ratingComment?: string;
		createdAt: string;
	}>;
	messages: Array<{
		id: string;
		sender: string;
		content: unknown;
		attachments?: string[];
		createdAt: string;
	}>;
	/** Pièces de la conversation (fichiers, vocaux, livraisons, vidéos) : métadonnées, liens demandés à l'ouverture. */
	chatMedia?: AdminChatMedia;
	deliveries: Array<{
		id: string;
		message?: unknown;
		/** Texte de l'aperçu offert au client avant l'acceptation. */
		previewMessage?: unknown;
		/** Montant annoncé avec la livraison, en unités mineures. */
		finalAmount?: number;
		viewedAt?: string;
		acceptedAt?: string;
		rejectedAt?: string;
		rejectionReason?: string;
		autoAcceptAt?: string;
		createdAt: string;
		files: Array<{
			id: string;
			originalName: string;
			mimeType: string;
			size: number;
			url: string;
		}>;
	}>;
}

/** Relectures d'un dossier dont la langue choisie se prépare : délai et nombre bornés. */
const TRANSLATION_RETRY_MS = 5000;
const MAX_TRANSLATION_RETRIES = 12;

export function ChatDetailPage() {
	const { t, locale } = useI18n();
	const { formatCents, formatUnits } = useMoneyFormatter();
	const { chatId } = useParams<{ chatId: string }>();
	const navigate = useNavigate();
	const [data, setData] = useState<ChatDetails | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [impersonating, setImpersonating] = useState<string | null>(null);

	// Mediation resolution state
	const [supportMessage, setSupportMessage] = useState("");
	const [reportReady, setReportReady] = useState(false);
	const [resolving, setResolving] = useState(false);
	const [resolveError, setResolveError] = useState("");
	const [confirmDialog, setConfirmDialog] = useState<MediationDecision | null>(null);
	// Somme que le support choisit de rendre, saisie dans l'unité courante.
	const [refundInput, setRefundInput] = useState("");
	const [resolveSuccess, setResolveSuccess] = useState<ResolveResult | null>(null);
	// Élément du fil amené à l'écran par une source du rapport, de la chronologie ou du prix
	const [conversationFocus, setConversationFocus] = useState<ConversationFocus | null>(null);
	// Pièces de la conversation : le support les ouvre dans le lecteur des parties, sans se connecter en tant qu'elles
	const attachments = useAttachmentViewer(chatId ?? "");
	// Incrémenté pour relire le détail, par exemple après une réouverture
	const [reloadKey, setReloadKey] = useState(0);
	// La version de référence est la vue par défaut du support (LANG-3, T10)
	const [viewMode, setViewModeState] = useState<TranslationViewMode>("reference");

	// La langue du support n'est demandée au serveur que lorsqu'elle est choisie (MEDIATIONUX-3)
	const requestedLocale = viewMode === "mine" ? locale : undefined;
	// Lecture de la langue choisie en cours de préparation : nombre de relectures déjà faites
	const [pendingRetries, setPendingRetries] = useState(0);
	const setViewMode = (mode: TranslationViewMode) => {
		setPendingRetries(0);
		setViewModeState(mode);
	};

	useEffect(() => {
		let cancelled = false;
		const fetchDetails = async () => {
			if (!chatId) return;
			// Une relecture ne remplace pas la page par un écran de chargement
			if (!data) setLoading(true);
			try {
				const query = requestedLocale ? `?locale=${encodeURIComponent(requestedLocale)}` : "";
				const result = await adminApi.get<ChatDetails>(`/admin/chats/${chatId}${query}`);
				if (!cancelled) setData(result);
			} catch (err) {
				if (!cancelled) setError(err instanceof Error ? err.message : t("adminChatDetail.loadFailed"));
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		fetchDetails();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatId, reloadKey, requestedLocale]);

	// Tant que des traductions de la langue choisie se préparent, le détail est relu à intervalle borné
	const translationsMissing = viewMode === "mine" && Boolean(data?.mediationTranslations?.entries.some(
		(entry) => entry.sourceLocale !== locale && entry.translations[locale]?.state !== "ready",
	));
	useEffect(() => {
		if (!translationsMissing || pendingRetries >= MAX_TRANSLATION_RETRIES) return undefined;
		const timer = window.setTimeout(() => {
			setPendingRetries((count) => count + 1);
			setReloadKey((key) => key + 1);
		}, TRANSLATION_RETRY_MS);
		return () => window.clearTimeout(timer);
	}, [translationsMissing, pendingRetries, data]);

	const formatDate = (date?: string) => {
		if (!date) return "N/A";
		return new Date(date).toLocaleString();
	};

	const handleImpersonate = async (userId: string, role: string) => {
		setImpersonating(role);
		try {
			const result = await adminApi.post<{ url: string }>(`/admin/users/${userId}/impersonate`, {
				redirectTo: `/chat/${chatId}`,
			});
			window.open(result.url, "_blank");
		} catch (err) {
			alert(err instanceof Error ? err.message : t("adminChatDetail.impersonateExpertFailed"));
		} finally {
			setImpersonating(null);
		}
	};

	const handleImpersonateGuest = async () => {
		if (!chatId) return;
		setImpersonating("client");
		try {
			const result = await adminApi.post<{ url: string }>(`/admin/chats/${chatId}/impersonate-guest`, {
				redirectTo: `/chat/${chatId}`,
			});
			window.open(result.url, "_blank");
		} catch (err) {
			alert(err instanceof Error ? err.message : t("adminChatDetail.impersonateGuestFailed"));
		} finally {
			setImpersonating(null);
		}
	};

	const handleResolve = async (decision: MediationDecision) => {
		if (!currentOrder?.id || !supportMessage.trim()) {
			setResolveError(t("adminChatDetail.supportMessageRequired"));
			return;
		}

		setResolving(true);
		setResolveError("");
		setConfirmDialog(null);

		try {
			const result = await adminApi.post<ResolveResult>(
				`/admin/mediations/${currentOrder.id}/resolve`,
				{
					decision,
					message: supportMessage.trim(),
					// Le montant n'a de sens qu'en faveur du client sur une vente
					// encaissée ; ailleurs, il n'y a rien à rendre.
					...(decision === "favor_client" && isSettled ? { refundAmount: plannedRefund } : {}),
				},
			);

			setResolveSuccess(result);
		} catch (err) {
			setResolveError(err instanceof Error ? err.message : t("adminChatDetail.resolveFailed"));
		} finally {
			setResolving(false);
		}
	};

	const renderMessageContent = (content: unknown): string => {
		if (typeof content === "string") {
			return content;
		}
		if (content && typeof content === "object") {
			const extractText = (node: unknown): string => {
				if (!node) return "";
				if (typeof node === "string") return node;
				if (typeof node !== "object") return String(node);
				const n = node as { type?: string; text?: string; content?: unknown };
				if (n.type === "text" && typeof n.text === "string") return n.text;
				if (n.content) {
					if (Array.isArray(n.content)) {
						return n.content.map(extractText).join("");
					}
					return extractText(n.content);
				}
				return "";
			};
			const result = extractText(content);
			return result || JSON.stringify(content);
		}
		return content == null ? "" : String(content);
	};

	/*
	 * Texte à afficher pour un élément du dossier : sa traduction quand elle est prête dans la
	 * langue choisie, sinon l'original, toujours accompagné de ce qui a été fait.
	 */
	const shown = (key: TextKey, original: unknown) => {
		const picked = pickTranslation(data?.mediationTranslations, viewMode, key, locale);
		const text = picked.kind === "translated" ? renderMessageContent(picked.content) : renderMessageContent(original);
		return { text, note: <TranslationNote picked={picked} /> };
	};

	if (loading) {
		return <div className="loading">{t("adminChatDetail.loading")}</div>;
	}

	if (error || !data) {
		return (
			<div>
				<div className="detail-header">
					<Link to="/chats" className="back-link">
						{t("adminChatDetail.backToChats")}
					</Link>
				</div>
				<div className="error-message">{error || t("adminChatDetail.notFound")}</div>
			</div>
		);
	}

	const currentOrder = data.orders[0];
	/*
	 * Une médiation reste ouverte tant que le support ne l'a pas tranchée. La
	 * condition portait auparavant sur une commande encore autorisée, ce qui
	 * revenait à masquer toute issue sur une vente déjà payée : le support voyait
	 * le litige et ne pouvait rien en faire.
	 */
	const newMessageIds = new Set(data.mediationCase?.newMessageIds ?? []);
	const newDeliveryIds = new Set(data.mediationCase?.newDeliveryIds ?? []);
	const hasPendingMediation = Boolean(currentOrder?.mediationRequestedAt && !currentOrder?.supportDecision);
	// Une vente encaissée se rembourse ; une vente seulement réservée se libère.
	const isSettled = currentOrder?.status === "captured";
	// Ce qui reste acquis à l'expert, et donc le maximum que le support peut rendre.
	const remainingSettled = Math.max(
		0,
		(currentOrder?.capturedAmount ?? currentOrder?.amount ?? 0) - (currentOrder?.refundedAmount ?? 0),
	);
	const media: AdminChatMedia = data.chatMedia ?? { files: [], videos: [] };
	// Le dossier chronologique n'existe que pour une conversation en médiation ; les autres
	// gardent la même disposition, avec le fil reconstitué sans événements de médiation
	const hasDossier = Boolean(data.mediationDossier && currentOrder?.mediationRequestedAt);
	// Ce que l'expert encaisserait en clôturant sur une vente seulement réservée : le montant annoncé par la livraison
	const captureAmount = data.mediationDossier?.price.agreedAmount ?? currentOrder?.amount ?? 0;
	// Le rapport suit la langue de consultation choisie une fois pour toute la page
	const reportLocale = viewMode === "dossier" && data.mediationTranslations
		? data.mediationTranslations.dossierLocale
		: viewMode === "mine"
			? locale
			: (data.mediationTranslations?.referenceLocale ?? data.mediationReport?.locale ?? locale);
	// Saisie vide : la totalité de ce qui reste acquis est rendue.
	const plannedRefund = refundInput.trim() === ""
		? remainingSettled
		: Math.round(Number(refundInput.replace(",", ".")) * 100);
	const isRefundValid = Number.isFinite(plannedRefund) && plannedRefund > 0 && plannedRefund <= remainingSettled;

	/*
	 * Récapitulatif de la décision rendue, lu sur ses propres faits financiers.
	 *
	 * Chaque ligne nomme une somme différente : ce que le support vient de rendre,
	 * ce qui reste acquis à l'expert, et la part de l'autorisation qui n'a jamais
	 * été encaissée. Cette dernière n'est jamais présentée comme un remboursement.
	 */
	const renderResolutionSummary = (result: ResolveResult, currency: string) => {
		const outcome = result.outcome;
		if (!outcome) {
			// Décision rendue par une version antérieure à ce dispositif : aucun
			// montant propre n'existe, et en inventer un serait le défaut d'origine.
			return null;
		}

		const rows: Array<{ label: string; value: number }> = [];
		if (outcome.gesture === "refund") {
			rows.push({ label: t("adminChatDetail.refunded"), value: outcome.refundedAmount });
			rows.push({ label: t("adminChatDetail.amountKept"), value: outcome.netAmountAfter });
		} else if (outcome.gesture === "capture") {
			rows.push({ label: t("adminChatDetail.captured2"), value: outcome.capturedAmountBefore });
		} else if (outcome.gesture === "none") {
			rows.push({ label: t("adminChatDetail.amountKept"), value: outcome.netAmountAfter });
		}
		if (outcome.releasedAmount > 0) {
			rows.push({ label: t("adminChatDetail.amountReleased"), value: outcome.releasedAmount });
		}

		if (rows.length === 0) {
			return (
				<p className="summary-note">
					{outcome.gesture === "authorization_release"
						? t("adminChatDetail.resolutionReleasedOnly")
						: t("adminChatDetail.resolutionNothingMoved")}
				</p>
			);
		}

		return rows.map((row) => (
			<div className="summary-row" key={row.label}>
				<span>{row.label}</span>
				<strong>{formatCents(row.value, currency)}</strong>
			</div>
		));
	};

	const banner = (
		<>
				{hasPendingMediation && (
					<div className="mediation-banner">
						<div className="mediation-banner-header">
							<i className="fas fa-gavel"></i>
							<span>
								{t("adminChatDetail.mediationRequestedBy")} <strong>{currentOrder.mediationRequestedBy}</strong>
							</span>
							<span className="mediation-date">{formatDate(currentOrder.mediationRequestedAt)}</span>
						</div>
						{currentOrder.mediationReason && (
							<blockquote className="mediation-reason-quote">
								{shown({ objectType: "order", objectId: currentOrder.id, field: "mediationReason" }, currentOrder.mediationReason).text}
								{shown({ objectType: "order", objectId: currentOrder.id, field: "mediationReason" }, currentOrder.mediationReason).note}
							</blockquote>
						)}
					</div>
				)}
		</>
	);

	const casePanel = (
		<>
				{data.mediationCase && currentOrder && (
					<MediationCasePanel
						orderId={currentOrder.id}
						mediationCase={data.mediationCase}
						decisionText={(round, original) => shown({ objectType: "mediation_round", objectId: `${currentOrder.id}:${round}`, field: "decisionMessage" }, original)}
						onReopened={() => {
								// Le message et le montant de la décision précédente ne valent pas pour le nouveau tour
								setSupportMessage("");
								setRefundInput("");
								setReloadKey((key) => key + 1);
							}}
					/>
				)}
		</>
	);

	const langBar = (
		<>
				{data.mediationTranslations && (
					<MediationTranslationBar view={data.mediationTranslations} mode={viewMode} onChange={setViewMode} uiLocale={locale} />
				)}
		</>
	);

	const infoCards = (
		<>
				<div className="detail-grid">
					{/* Chat Info */}
					<div className="info-card">
						<h3>{t("adminChatDetail.chatInfo")}</h3>
						<div className="info-row">
							<span className="info-label">{t("adminChatDetail.id")}</span>
							<span className="info-value" style={{ fontSize: "0.75rem" }}>
								{data.chat.id}
							</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t("adminChatDetail.status")}</span>
							<span className="info-value">
								<span className={`status-badge ${data.chat.status}`}>
									{data.chat.status}
								</span>
							</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t("adminChatDetail.created")}</span>
							<span className="info-value">{formatDate(data.chat.createdAt)}</span>
						</div>
						{data.chat.acceptedAt && (
							<div className="info-row">
								<span className="info-label">{t("adminChatDetail.accepted")}</span>
								<span className="info-value">{formatDate(data.chat.acceptedAt)}</span>
							</div>
						)}
						{data.chat.completedAt && (
							<div className="info-row">
								<span className="info-label">{t("adminChatDetail.completed")}</span>
								<span className="info-value">{formatDate(data.chat.completedAt)}</span>
							</div>
						)}
					</div>

					{/* Expert Info */}
					<div className="info-card">
						<h3>{t("adminChatDetail.expert")}</h3>
						<div className="info-row">
							<span className="info-label">{t("adminChatDetail.name")}</span>
							<span className="info-value">
								<Link to={`/users/${data.expert.id}`} className="table-link">
									{data.expert.displayName ||
										`${data.expert.firstName} ${data.expert.lastName}`}
								</Link>
							</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t("adminChatDetail.email")}</span>
							<span className="info-value">{data.expert.email}</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t("adminChatDetail.rating")}</span>
							<span className="info-value">
								{data.expert.averageRating
									? t("adminUserDetail.ratingValue", { rating: data.expert.averageRating, count: data.expert.totalRatings ?? 0 })
									: t("adminChatDetail.noRatings")}
							</span>
						</div>
						<button
							className="btn btn-sm btn-outline"
							onClick={() => handleImpersonate(data.expert.id, "expert")}
							disabled={impersonating !== null}
							style={{ marginTop: "0.75rem", width: "100%" }}
						>
							{impersonating === "expert" ? t("adminChatDetail.opening") : t("adminChatDetail.viewAsExpert")}
						</button>
					</div>

					{/* Client Info */}
					<div className="info-card">
						<h3>{t("adminChatDetail.client")}</h3>
						{data.client.isGuest ? (
							<>
								<div className="info-row">
									<span className="info-label">{t("adminChatDetail.email")}</span>
									<span className="info-value">{data.client.email}</span>
								</div>
								<div className="info-row">
									<span className="info-label">{t("adminChatDetail.name")}</span>
									<span className="info-value">{data.client.name || t("adminChatDetail.notProvided")}</span>
								</div>
								<div className="info-row">
									<span className="info-label">{t("adminChatDetail.account")}</span>
									<span className="info-value">
										<span className="status-badge pending">{t("adminChatDetail.guest")}</span>
									</span>
								</div>
								<button
									className="btn btn-sm btn-outline"
									onClick={handleImpersonateGuest}
									disabled={impersonating !== null}
									style={{ marginTop: "0.75rem", width: "100%" }}
								>
									{impersonating === "client" ? t("adminChatDetail.opening") : t("adminChatDetail.viewAsGuest")}
								</button>
							</>
						) : (
							<>
								<div className="info-row">
									<span className="info-label">{t("adminChatDetail.name")}</span>
									<span className="info-value">
										{data.client.id ? (
											<Link to={`/users/${data.client.id}`} className="table-link">
												{data.client.firstName} {data.client.lastName}
											</Link>
										) : (
											`${data.client.firstName} ${data.client.lastName}`
										)}
									</span>
								</div>
								<div className="info-row">
									<span className="info-label">{t("adminChatDetail.email")}</span>
									<span className="info-value">{data.client.email}</span>
								</div>
								<div className="info-row">
									<span className="info-label">{t("adminChatDetail.memberSince")}</span>
									<span className="info-value">{formatDate(data.client.createdAt)}</span>
								</div>
								{data.client.id && (
									<button
										className="btn btn-sm btn-outline"
										onClick={() => handleImpersonate(data.client.id!, "client")}
										disabled={impersonating !== null}
										style={{ marginTop: "0.75rem", width: "100%" }}
									>
										{impersonating === "client" ? t("adminChatDetail.opening") : t("adminChatDetail.viewAsClient")}
									</button>
								)}
							</>
						)}
					</div>

					{/* Product Info */}
					<div className="info-card">
						<h3>{t("adminChatDetail.product")}</h3>
						<div className="info-row">
							<span className="info-label">{t("adminChatDetail.name")}</span>
							<span className="info-value">{data.product.name}</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t("adminChatDetail.price")}</span>
							<span className="info-value">
								{formatUnits(data.product.price, data.product.currency)}
							</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t("adminChatDetail.maxDelivery")}</span>
							<span className="info-value">{t("adminChatDetail.hours", { count: data.product.maxDeliveryHours })}</span>
						</div>
					</div>
				</div>
		</>
	);

	const ordersTable = (
		<>
				{data.orders.length > 0 && (
					<div className="card" style={{ marginBottom: "2rem" }}>
						<div className="card-header">
							<h2>{t("adminChatDetail.orders", { count: data.orders.length })}</h2>
						</div>
						<div className="table-container">
							<table>
								<thead>
									<tr>
										<th>{t("adminChatDetail.amount")}</th>
										<th>{t("adminChatDetail.status")}</th>
										<th>{t("adminChatDetail.authorized")}</th>
										<th>{t("adminChatDetail.captured")}</th>
										<th>{t("adminChatDetail.rating")}</th>
									</tr>
								</thead>
								<tbody>
									{data.orders.map((order) => (
										<tr key={order.id}>
											<td>{formatCents(order.amount, order.currency)}</td>
											<td>
												<span className={`status-badge ${order.status}`}>
													{order.status}
												</span>
												{order.mediationRequestedAt && (
													<span
														className={`status-badge ${order.supportDecision ? "completed" : "mediation"}`}
														style={{ marginLeft: "0.25rem" }}
													>
														{order.supportDecision
															? t("adminChatsPage.mediationResolved")
															: t("adminChatsPage.mediation")}
													</span>
												)}
											</td>
											<td>{formatDate(order.authorizedAt)}</td>
											<td>{formatDate(order.capturedAt)}</td>
											<td>
												{order.rating ? `${order.rating}/5` : t("adminChatDetail.noRating")}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				)}
		</>
	);

	const deliveryHistory = (
		<>
				{currentOrder?.deliveryHistory && currentOrder.deliveryHistory.length > 0 && (
					<div className="card" style={{ marginBottom: "2rem" }}>
						<div className="card-header">
							<h2>{t("adminChatDetail.deliveryHistory")}</h2>
						</div>
						<div className="card-body">
							<div className="delivery-timeline">
								{currentOrder.deliveryHistory.map((event, index) => (
									<div key={index} className={`timeline-item ${event.action}`}>
										<div className="timeline-icon">
											{event.action === "delivered" && <i className="fas fa-truck"></i>}
											{event.action === "redelivered" && <i className="fas fa-redo"></i>}
											{event.action === "rejected" && <i className="fas fa-times-circle"></i>}
											{event.action === "confirmed" && <i className="fas fa-check-circle"></i>}
											{event.action === "mediation_requested" && <i className="fas fa-gavel"></i>}
											{event.action === "viewed" && <i className="fas fa-eye"></i>}
											{event.action === "auto_accepted" && <i className="fas fa-clock"></i>}
											{!["delivered", "redelivered", "rejected", "confirmed", "mediation_requested", "viewed", "auto_accepted"].includes(event.action) && <i className="fas fa-circle"></i>}
										</div>
										<div className="timeline-content">
											<div className="timeline-action">
												{DELIVERY_ACTION_KEYS[event.action] ? t(DELIVERY_ACTION_KEYS[event.action]) : event.action.replace(/_/g, " ")}
											</div>
											<div className="timeline-meta">
												<span className="timeline-by">{t("adminChatDetail.by", { name: event.by })}</span>
												<span className="timeline-time">{formatDate(event.timestamp)}</span>
											</div>
											{event.reason && (
												<div className="timeline-reason">"{event.reason}"</div>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				)}
		</>
	);

	const deliveriesList = (
		<>
				{data.deliveries.length > 0 && (
					<CollapsibleCard className="deliveries-card" id="mediation-deliveries" title={t("adminChatDetail.deliveriesCount", { count: data.deliveries.length })}>
						<div>
							{data.deliveries.map((delivery) => (
								<div key={delivery.id} id={`delivery-${delivery.id}`} className="delivery-card">
									<div className="delivery-header">
										<strong>{t("adminChatDetail.deliveryOn", { date: formatDate(delivery.createdAt) })}</strong>
										{newDeliveryIds.has(delivery.id) && <span className="new-badge">{t("adminChatDetail.mediationCase.newBadge")}</span>}
										<div>
											{delivery.acceptedAt && (
												<span className="status-badge completed">{t("adminChatDetail.accepted2")}</span>
											)}
											{delivery.rejectedAt && (
												<span className="status-badge declined">{t("adminChatDetail.rejected")}</span>
											)}
											{!delivery.acceptedAt && !delivery.rejectedAt && (
												<span className="status-badge pending">{t("adminChatDetail.pending")}</span>
											)}
										</div>
									</div>
									{delivery.finalAmount !== undefined && currentOrder && (
										<p className="conversation-meta">{t("adminChatDetail.conversation.deliveryAmount", { amount: formatCents(delivery.finalAmount, currentOrder.currency) })}</p>
									)}
									{delivery.message !== undefined && delivery.message !== null && (
										<p style={{ marginBottom: "0.75rem", color: "var(--color-gray-600)" }}>
											{shown({ objectType: "delivery", objectId: delivery.id, field: "message" }, delivery.message).text}
											{shown({ objectType: "delivery", objectId: delivery.id, field: "message" }, delivery.message).note}
										</p>
									)}
									{delivery.previewMessage !== undefined && delivery.previewMessage !== null && (
										<div className="delivery-preview-text">
											<strong>{t("adminChatDetail.media.previewFile")} : </strong>
											{renderMessageContent(delivery.previewMessage)}
										</div>
									)}
									{delivery.rejectionReason && (
										<p
											style={{
												marginBottom: "0.75rem",
												color: "var(--color-danger)",
												fontStyle: "italic",
											}}
										>
											{t("adminChatDetail.rejectionReason", { reason: shown({ objectType: "delivery", objectId: delivery.id, field: "rejectionReason" }, delivery.rejectionReason).text })}
											{shown({ objectType: "delivery", objectId: delivery.id, field: "rejectionReason" }, delivery.rejectionReason).note}
										</p>
									)}
									<MediaLinks
										files={media.files.filter((file) => file.deliveryId === delivery.id)}
										videos={media.videos.filter((video) => video.deliveryId === delivery.id)}
										busyId={attachments.busyId}
										onOpenFile={attachments.openFile}
										onOpenVideo={attachments.openVideo}
									/>
								</div>
							))}
						</div>
					</CollapsibleCard>
				)}
		</>
	);

	// Fil reconstitué, tel que le voient les deux parties, événements compris
	const dossierView = hasDossier ? data.mediationDossier : undefined;
	const conversationCurrency = dossierView?.price.currency ?? currentOrder?.currency ?? data.product.currency;
	const deliveryAmounts: Record<string, number> = dossierView
		? Object.fromEntries(dossierView.price.steps.filter((step) => step.deliveryId).map((step) => [step.deliveryId as string, step.amount]))
		: Object.fromEntries(data.deliveries.filter((delivery) => delivery.finalAmount !== undefined).map((delivery) => [delivery.id, delivery.finalAmount as number]));
	const conversationItems = buildConversation({
		chat: { id: data.chat.id, requestMessage: data.chat.requestMessage, createdAt: data.chat.createdAt },
		messages: data.messages,
		deliveries: data.deliveries,
		media,
		timeline: dossierView?.timeline ?? [],
		deliveryAmounts,
	});
	const conversationRefs = new Set(conversationItems.map((item) => item.ref));
	const canOpenRef = (ref: string) => conversationRefs.has(conversationRef(ref));
	const openRef = (ref: string) => {
		if (canOpenRef(ref)) setConversationFocus((previous) => ({ ref, nonce: (previous?.nonce ?? 0) + 1 }));
	};
	// Texte joint à un événement : motif de la médiation du premier tour, message de chaque décision
	const eventText = (ref: string) => {
		const opened = /^event:round-(\d+)-opened$/.exec(ref);
		if (opened && opened[1] === "1" && currentOrder?.mediationReason) {
			return { label: "adminChatDetail.conversation.mediationReason" as MessageKey, ...shown({ objectType: "order", objectId: currentOrder.id, field: "mediationReason" }, currentOrder.mediationReason) };
		}
		const decided = /^event:round-(\d+)-decided$/.exec(ref);
		if (decided && currentOrder) {
			const round = data.mediationCase?.rounds.find((candidate) => candidate.number === Number(decided[1]));
			if (round?.decision?.message) {
				return { label: "adminChatDetail.conversation.decisionMessage" as MessageKey, ...shown({ objectType: "mediation_round", objectId: `${currentOrder.id}:${round.number}`, field: "decisionMessage" }, round.decision.message) };
			}
		}
		return null;
	};
	const conversationPanel = (
		<MediationConversationPanel
			items={conversationItems}
			currency={conversationCurrency}
			focus={conversationFocus}
			newMessageIds={newMessageIds}
			newDeliveryIds={newDeliveryIds}
			renderText={shown}
			eventText={eventText}
			media={{ busyId: attachments.busyId, onOpenFile: attachments.openFile, onOpenVideo: attachments.openVideo }}
		/>
	);

	const mediationTop = hasDossier && data.mediationDossier && currentOrder ? (
		<>
			<MediationSnapshot dossier={data.mediationDossier} attachments={media.files.length + media.videos.length} />
			{data.mediationReport && (
				<MediationReportPanel
					orderId={currentOrder.id}
					currency={data.mediationDossier.price.currency}
					initial={data.mediationReport}
					locale={reportLocale}
					onReadyChange={setReportReady}
					canOpenRef={canOpenRef}
					onOpenRef={openRef}
					consult={{
						// Ce que l'IA ne sait pas lire et que le support juge : pièces et aperçus de livraison, vidéos livrées
						files: media.files.filter((file) => file.kind !== "attachment"),
						videos: media.videos.filter((video) => video.deliveryId),
						busyId: attachments.busyId,
						onOpenFile: attachments.openFile,
						onOpenVideo: attachments.openVideo,
					}}
				/>
			)}
			<MediationPricePanel price={data.mediationDossier.price} onOpenRef={openRef} />
		</>
	) : null;

	const timelinePanel = hasDossier && data.mediationDossier ? (
		<MediationTimelinePanel timeline={data.mediationDossier.timeline} currency={data.mediationDossier.price.currency} canOpenRef={canOpenRef} onOpenRef={openRef} />
	) : null;

	const decisionPanel = hasPendingMediation && currentOrder ? (
		<MediationDecisionPanel
			orderId={currentOrder.id}
			currency={currentOrder.currency}
			isSettled={isSettled}
			remainingSettled={remainingSettled}
			authorizedAmount={currentOrder.amount}
			captureAmount={captureAmount}
			reportReady={reportReady}
			reportLocale={reportLocale}
			message={supportMessage}
			onMessageChange={setSupportMessage}
			refundInput={refundInput}
			onRefundInputChange={setRefundInput}
			plannedRefund={plannedRefund}
			isRefundValid={isRefundValid}
			resolving={resolving}
			error={resolveError}
			onChoose={setConfirmDialog}
		/>
	) : null;


	return (
		<div className="chat-detail-page mediation-workstation">
			<div className="detail-header">
				<Link to="/chats" className="back-link">
					{t("adminChatDetail.backToChats")}
				</Link>
				<h1>{t("adminChatDetail.title")}</h1>
				<span className={`status-badge ${data.chat.status}`} style={{ marginLeft: "1rem" }}>
					{data.chat.status}
				</span>
				{currentOrder?.mediationRequestedAt && (
					<span
						className={`status-badge ${hasPendingMediation ? "mediation" : "completed"}`}
						style={{ marginLeft: "0.5rem" }}
					>
						{hasPendingMediation
							? t("adminChatDetail.inMediation")
							: t("adminChatsPage.mediationResolved")}
					</span>
				)}
			</div>

			<ResizableColumns
				left={
					hasDossier ? (
						<>
							{banner}
							{langBar}
							{mediationTop}
							{casePanel}
							{timelinePanel}
							{deliveriesList}
							<details className="mediation-more">
								<summary>{t("adminChatDetail.dossier.moreInfo")}</summary>
								{infoCards}
								{ordersTable}
							</details>
							{decisionPanel}
						</>
					) : (
						<>
							{banner}
							{casePanel}
							{langBar}
							{infoCards}
							{ordersTable}
							{deliveryHistory}
							{deliveriesList}
							{decisionPanel}
						</>
					)
				}
				right={conversationPanel}
			/>

			{attachments.viewer}

			{/* Confirmation Dialog */}
			{confirmDialog && currentOrder && (
				<div className="modal-overlay" onClick={() => setConfirmDialog(null)}>
					<div className="modal-content" style={{ maxWidth: "500px" }} onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h2>{t("adminChatDetail.confirmResolution")}</h2>
							<button className="modal-close" onClick={() => setConfirmDialog(null)}>
								&times;
							</button>
						</div>
						<div className="modal-body">
							<div className={`confirmation-summary ${confirmDialog}`}>
								{confirmDialog === "favor_client" ? (
									<>
										<i className="fas fa-undo-alt"></i>
										<h3>{isSettled && plannedRefund < remainingSettled
											? t("adminChatDetail.favorClientPartialRefund")
											: t("adminChatDetail.favorClientFullRefund")}</h3>
										{/*
										  * L'explication suit le titre : avant l'encaissement le
										  * paiement s'annule, après il se rembourse — en totalité
										  * ou pour la seule part décidée par le support.
										  */}
										<p>{!isSettled
											? t("adminChatDetail.favorClientBody")
											: plannedRefund < remainingSettled
												? t("adminChatDetail.favorClientSettledPartialBody")
												: t("adminChatDetail.favorClientSettledFullBody")}</p>
										<div className="amount-summary">
											<div className="amount-row">
												<span>{t("adminChatDetail.clientReceives")}</span>
												<strong>
													{formatCents(
														isSettled ? plannedRefund : currentOrder.amount,
														currentOrder.currency,
													)}
												</strong>
											</div>
											<div className="amount-row">
												<span>{t("adminChatDetail.expertReceives")}</span>
												<strong>
													{formatCents(
														isSettled ? remainingSettled - plannedRefund : 0,
														currentOrder.currency,
													)}
												</strong>
											</div>
										</div>
										<p className="review-note">
											<i className="fas fa-star"></i>
											{t("adminChatDetail.clientCanReview")}
										</p>
									</>
								) : (
									<>
										<i className="fas fa-check-circle"></i>
										<h3>{isSettled
											? t("adminChatDetail.favorExpertKeep")
											: t("adminChatDetail.favorExpertCapture")}</h3>
										<p>{isSettled
											? t("adminChatDetail.favorExpertKeepBody")
											: t("adminChatDetail.favorExpertBody")}</p>
										<div className="amount-summary">
											<div className="amount-row">
												<span>{isSettled
													? t("adminChatDetail.amountKept")
													: t("adminChatDetail.amountCaptured")}</span>
												<strong>
													{formatCents(
														isSettled ? remainingSettled : captureAmount,
														currentOrder.currency,
													)}
												</strong>
											</div>
										</div>
										<p className="review-note blocked">
											<i className="fas fa-info-circle"></i>
											{t("adminChatDetail.clientCannotReview")}
										</p>
									</>
								)}
							</div>
							<div className="message-preview">
								<label>{t("adminChatDetail.messagePreview")}</label>
								<blockquote>{supportMessage}</blockquote>
							</div>
						</div>
						<div className="modal-footer">
							<button
								className="btn btn-secondary"
								onClick={() => setConfirmDialog(null)}
								disabled={resolving}
							>
								{t("common.cancel")}
							</button>
							<button
								className={`btn ${confirmDialog === "favor_client" ? "btn-success" : "btn-primary"}`}
								onClick={() => handleResolve(confirmDialog)}
								disabled={resolving}
							>
								{resolving ? t("adminChatDetail.processing") : t("adminChatDetail.confirmResolution")}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Success Modal */}
			{resolveSuccess && (
				<div className="modal-overlay">
					<div className="modal-content" style={{ maxWidth: "450px", textAlign: "center" }}>
						<div style={{ padding: "1.5rem" }}>
							<div className={`success-icon ${resolveSuccess.decision}`}>
								<i className="fas fa-check"></i>
							</div>
							<h2 style={{ marginBottom: "1rem" }}>
								{t("adminChatDetail.mediationResolved")}
							</h2>
							<p style={{ color: "var(--color-gray-600)", marginBottom: "1.5rem" }}>
								{resolveSuccess.decision === "favor_client"
									? t("adminChatDetail.clientRefunded")
									: t("adminChatDetail.fundsCaptured")}
							</p>
							<div className="success-summary">
								{renderResolutionSummary(resolveSuccess, currentOrder?.currency || "eur")}
							</div>
							<button
								className="btn btn-primary"
								onClick={() => navigate("/chats?mediation=pending")}
								style={{ width: "100%", marginTop: "1.5rem" }}
							>
								{t("adminChatDetail.backToPendingMediations")}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
